import { usePage } from "@inertiajs/react";
import { format } from "date-fns";
import { enUS, id as idLocale } from "date-fns/locale";
import { AnimatePresence, motion } from "framer-motion";
import {
	AlertCircle,
	ArrowDownIcon,
	ArrowUpIcon,
	CalendarClockIcon,
	ChevronDown,
	ChevronUp,
	RefreshCcwIcon,
	StethoscopeIcon,
	X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DateTimePicker from "@/components/admin-portal/appointment/form/date-time";
import { SelectedTherapistCard } from "@/components/admin-portal/appointment/form/selected-therapist-card";
import { TherapistSearchList } from "@/components/admin-portal/appointment/form/therapist-search-list";
import {
	type ScheduleMode,
	ScheduleModeToggle,
} from "@/components/admin-portal/scheduling/schedule-mode-toggle";
import { SchedulePicker } from "@/components/admin-portal/scheduling/schedule-picker";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import type { BulkRescheduleItemSchema } from "@/lib/appointments/form-bulk-reschedule";
import type { EmploymentType } from "@/lib/therapist-scheduling/constants";
import {
	slotSelectionToDateTime,
	slotSelectionToTherapist,
	toSlotSelection,
} from "@/lib/therapist-scheduling/form-adapter";
import { buildReschedulePickerProps } from "@/lib/therapist-scheduling/reschedule-picker-props";
import type { SlotSelection } from "@/lib/therapist-scheduling/types";
import { cn } from "@/lib/utils";
import type { Appointment } from "@/types/admin-portal/appointment";
import type { TherapistFeasible } from "@/types/admin-portal/therapist";
import type { GlobalPageProps } from "@/types/globals";

interface RescheduleAppointmentRowProps {
	appointment: Appointment & {
		// Serialized by PreparationBulkRescheduleService for the calendar picker.
		locationId?: number | string | null;
		serviceId?: number | string | null;
		latitude?: number | null;
		longitude?: number | null;
	};
	field: BulkRescheduleItemSchema;
	index: number;
	optionsData: {
		preferredTherapistGender: string[];
		apptDateTime: {
			min: string | null;
			max: string | null;
			message: string | null;
		};
	};
	onUpdateField: (index: number, field: string, value: unknown) => void;
	onProcessDateTimeChange: (index: number, newDateTime: Date | null) => void;
	currentVisitNumber: number;
	/** ISO dates held by the OTHER rows — struck through in this row's calendar. */
	otherScheduledDates: string[];
	/**
	 * Therapist the calendar is coloured for. Resolved by the page: this visit's own
	 * therapist, else the one visit 1 / the previous visit carries — so a visit 2+
	 * that has no therapist yet still gets a coloured calendar.
	 */
	continuityTherapist: BulkRescheduleItemSchema["therapist"];
}

type ResetAction = "appointmentDateTime" | "therapist" | "all";

export function RescheduleAppointmentRow({
	appointment,
	field,
	index,
	optionsData,
	onUpdateField,
	onProcessDateTimeChange,
	currentVisitNumber,
	otherScheduledDates,
	continuityTherapist,
}: RescheduleAppointmentRowProps) {
	const { t, i18n } = useTranslation("appointments-form");
	const currentLocale = i18n.language === "id" ? idLocale : enUS;

	const [isExpanded, setIsExpanded] = useState(false);
	const [scheduleMode, setScheduleMode] = useState<ScheduleMode>("picker");

	const { props: pageProps } = usePage<GlobalPageProps>();
	const useNewSchedulePicker =
		!!pageProps.adminPortal?.featureFlags?.useNewSchedulePickerEnabled;
	// Without a location, a service and coordinates the picker cannot query
	// availability, so that visit only gets the legacy flow (and no toggle).
	const pickerProps = useMemo(
		() =>
			buildReschedulePickerProps(
				{
					locationId: appointment.locationId,
					serviceId: appointment.serviceId,
					latitude: appointment.latitude,
					longitude: appointment.longitude,
				},
				{
					min: optionsData.apptDateTime.min,
					max: optionsData.apptDateTime.max,
				},
			),
		[appointment, optionsData.apptDateTime],
	);
	const showScheduleModeToggle = useNewSchedulePicker && !!pickerProps;
	const showPicker = showScheduleModeToggle && scheduleMode === "picker";
	const pickerSelection = useMemo(
		() => toSlotSelection(field.therapist, field.appointmentDateTime),
		[field.therapist, field.appointmentDateTime],
	);
	const [resetConfirmation, setResetConfirmation] =
		useState<ResetAction | null>(null);
	// Find-therapist mode commits on click, like the calendar picker — no separate
	// Apply step. Clicking a search result assigns the therapist to the row now.
	const handleSelectTherapist = useCallback(
		(therapist: TherapistFeasible) => {
			onUpdateField(index, "therapist", therapist);
		},
		[index, onUpdateField],
	);

	const toggleRow = () => {
		setIsExpanded(!isExpanded);
	};

	// DateTimePicker takes Dates; the bulk page ships these bounds as ISO strings.
	const minDate = useMemo(
		() =>
			optionsData.apptDateTime.min
				? new Date(optionsData.apptDateTime.min)
				: null,
		[optionsData.apptDateTime.min],
	);
	const maxDate = useMemo(
		() =>
			optionsData.apptDateTime.max
				? new Date(optionsData.apptDateTime.max)
				: null,
		[optionsData.apptDateTime.max],
	);

	/** Picker mode commits on selection — same ordering rationale as the legacy apply. */
	const applyPickerSelection = useCallback(
		(selection: SlotSelection | null) => {
			if (!selection) {
				// The parent's unschedule path clears the therapist too.
				onProcessDateTimeChange(index, null);
				return;
			}

			onUpdateField(index, "therapist", slotSelectionToTherapist(selection));
			onProcessDateTimeChange(index, slotSelectionToDateTime(selection));
		},
		[index, onProcessDateTimeChange, onUpdateField],
	);

	const applyReset = useCallback(
		(action: ResetAction) => {
			if (action === "appointmentDateTime" || action === "all") {
				onUpdateField(
					index,
					"appointmentDateTime",
					appointment.appointmentDateTime
						? new Date(appointment.appointmentDateTime)
						: null,
				);
			}

			if (action === "therapist" || action === "all") {
				onUpdateField(
					index,
					"therapist",
					appointment.therapist
						? {
								id: appointment.therapist.id,
								name: appointment.therapist.name,
								registrationNumber: appointment.therapist.registrationNumber,
								employmentType: appointment.therapist.employmentType,
								gender: appointment.therapist.gender,
								locationCity:
									appointment.therapist.activeAddress?.location?.city,
							}
						: null,
				);
				onUpdateField(
					index,
					"preferredTherapistGender",
					appointment.preferredTherapistGender ?? "NO PREFERENCE",
				);
			}
		},
		[appointment, onUpdateField, index],
	);

	const currentDateTimeStr = appointment.appointmentDateTime
		? new Date(appointment.appointmentDateTime).toISOString()
		: null;
	const selectedDateTimeStr = field.appointmentDateTime
		? field.appointmentDateTime.toISOString()
		: null;
	const isChanged =
		selectedDateTimeStr !== currentDateTimeStr ||
		field.therapist?.id !== appointment.therapist?.id;
	const isDateTimeModified = selectedDateTimeStr !== currentDateTimeStr;
	const isTherapistModified = field.therapist?.id !== appointment.therapist?.id;
	const hasAnyResetOption = isDateTimeModified || isTherapistModified;

	return (
		<div className="border rounded-lg overflow-hidden">
			<button
				type="button"
				className={cn(
					"w-full p-4 bg-background hover:bg-accent transition-colors",
					isChanged && "bg-amber-50 dark:bg-amber-950/30",
				)}
				onClick={toggleRow}
			>
				<div className="flex items-center justify-between gap-3 lg:gap-4 flex-1 flex-wrap">
					<div className="order-first w-fit flex items-center gap-2 md:w-32">
						<span className="font-bold text-base whitespace-nowrap">
							{t("bulk_reschedule.row.visit", "Visit")} {currentVisitNumber}
						</span>

						{Number(appointment.visitNumber) !== Number(currentVisitNumber) && (
							<Badge
								variant="outline"
								className={cn(
									"px-1.5 py-0 h-5 flex items-center gap-1 rounded-full shadow-none animate-in fade-in zoom-in-95 duration-300",
									Number(currentVisitNumber) < Number(appointment.visitNumber)
										? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400"
										: "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-400",
								)}
							>
								<span className="text-[10px] font-bold">
									{appointment.visitNumber}
								</span>
								{Number(currentVisitNumber) <
								Number(appointment.visitNumber) ? (
									<ArrowUpIcon className="size-2.5 stroke-[3]" />
								) : (
									<ArrowDownIcon className="size-2.5 stroke-[3]" />
								)}
							</Badge>
						)}
					</div>

					<div className="order-last md:order-1 w-full grid grid-cols-1 md:grid-cols-2 gap-1 md:gap-4 md:flex-1">
						<div className="flex items-center gap-2 text-sm text-left text-pretty">
							<div
								className={cn(
									"flex h-7 w-7 items-center justify-center rounded-full",
									isDateTimeModified
										? "bg-amber-100 border border-amber-200"
										: "bg-background border border-border",
								)}
							>
								<CalendarClockIcon
									className={cn(
										"h-4 w-4 shrink-0",
										isDateTimeModified
											? "text-amber-700"
											: "text-muted-foreground/75",
									)}
								/>
							</div>
							<div>
								<span
									className={cn(
										isDateTimeModified
											? "line-through italic text-muted-foreground/80"
											: "",
									)}
								>
									{appointment.appointmentDateTime
										? format(
												new Date(appointment.appointmentDateTime),
												"PPPPp",
												{
													locale: currentLocale,
												},
											)
										: t("statuses.unscheduled", "Unscheduled")}
								</span>

								{isDateTimeModified && (
									<div className="text-amber-700 dark:text-amber-400 font-medium">
										{field.appointmentDateTime
											? format(new Date(field.appointmentDateTime), "PPPPp", {
													locale: currentLocale,
												})
											: t("statuses.unscheduled", "Unscheduled")}
									</div>
								)}
							</div>
						</div>

						<div className="flex items-center gap-2 text-sm text-left text-pretty">
							<div
								className={cn(
									"flex h-7 w-7 items-center justify-center rounded-full",
									isTherapistModified
										? "bg-amber-100 border border-amber-200"
										: "bg-background border border-border",
								)}
							>
								<StethoscopeIcon
									className={cn(
										"h-4 w-4 shrink-0",
										isTherapistModified
											? "text-amber-700"
											: "text-muted-foreground/75",
									)}
								/>
							</div>
							<div>
								<span
									className={cn(
										isTherapistModified
											? "line-through italic text-muted-foreground/80"
											: "",
									)}
								>
									{appointment?.therapist?.name
										? appointment.therapist.name
										: t(
												"list.empty.no_therapist_assigned",
												"No therapist assigned",
											)}
								</span>

								{isTherapistModified && (
									<div className="text-amber-700 dark:text-amber-400 font-medium">
										{field?.therapist?.name ? (
											<span className="uppercase">{field.therapist.name}</span>
										) : (
											<span>
												{t(
													"list.empty.no_therapist_assigned",
													"No therapist assigned",
												)}
											</span>
										)}
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="md:order-2 flex items-center gap-2 shrink-0">
						<div>
							<Badge
								className={cn(
									"px-1 rounded-full",
									isChanged
										? "bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-200"
										: "bg-gray-100 text-gray-800 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200",
								)}
							>
								<span className="uppercase text-[10px]">
									{isChanged
										? t("bulk_reschedule.row.badges.modified", "Modified")
										: t("bulk_reschedule.row.badges.unchanged", "Unchanged")}
								</span>
							</Badge>
						</div>

						{isExpanded ? (
							<ChevronUp className="h-5 w-5" />
						) : (
							<ChevronDown className="h-5 w-5" />
						)}
					</div>
				</div>
			</button>

			<AnimatePresence>
				{isExpanded && (
					<motion.div
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: "auto", opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{ duration: 0.3, ease: "easeInOut" }}
						className="overflow-hidden border-t bg-accent"
					>
						<div className="p-4">
							<div className="rounded-md border bg-card p-4 space-y-6">
								{showScheduleModeToggle ? (
									<ScheduleModeToggle
										value={scheduleMode}
										onChange={setScheduleMode}
									/>
								) : null}

								{showPicker && pickerProps ? (
									<SchedulePicker
										latitude={pickerProps.latitude}
										longitude={pickerProps.longitude}
										locationId={pickerProps.locationId}
										serviceId={pickerProps.serviceId}
										minDate={pickerProps.minDate}
										maxDate={pickerProps.maxDate}
										gender={field.preferredTherapistGender ?? undefined}
										value={pickerSelection}
										onChange={applyPickerSelection}
										continuityTherapist={
											continuityTherapist
												? {
														id: String(continuityTherapist.id),
														name: continuityTherapist.name,
														employmentType:
															continuityTherapist.employmentType as
																| EmploymentType
																| undefined,
													}
												: null
										}
										// Bulk reschedule never resets sibling visits.
										shouldConfirmTherapistChange={false}
										scheduledDates={otherScheduledDates}
										splitLayout
									/>
								) : (
									<>
										{/* The visit's currently-assigned therapist stays visible here so
										    switching to search mode doesn't hide who is assigned. */}
										{field.therapist && (
											<SelectedTherapistCard
												therapist={field.therapist}
												onUnselect={() =>
													onUpdateField(index, "therapist", null)
												}
											/>
										)}

										<div className="space-y-4">
											<DateTimePicker
												timeVariant="select"
												value={field.appointmentDateTime}
												min={minDate}
												max={maxDate}
												onChangeValue={(newValue: Date | null) =>
													onProcessDateTimeChange(index, newValue)
												}
												isAllOfDay={false}
												allowPastTimes={!minDate}
											/>
										</div>

										<Separator />

										<TherapistSearchList
											instanceId={String(index)}
											appointmentId={String(appointment.id)}
											preferredTherapistGender={
												field.preferredTherapistGender ?? "NO PREFERENCE"
											}
											isAllOfDay={false}
											selectedTherapistId={field.therapist?.id ?? null}
											onSelect={handleSelectTherapist}
										/>
									</>
								)}

								{/* Row-level actions sit together at the foot of the editor: unschedule and
							    reset on the left, the legacy apply on the right. Picker mode commits on
							    slot click, so it needs no apply button. */}
								<Separator />

								<div className="flex flex-wrap items-center justify-between gap-2">
									<div className="flex items-center gap-1">
										{field.appointmentDateTime && (
											<Button
												type="button"
												variant="ghost"
												size="sm"
												className="text-muted-foreground"
												onClick={() => onProcessDateTimeChange(index, null)}
											>
												<X className="h-4 w-4" />
												{t("bulk_reschedule.row.unschedule", "Unschedule")}
											</Button>
										)}

										{hasAnyResetOption && (
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button
														type="button"
														variant="ghost-destructive"
														size="sm"
													>
														<RefreshCcwIcon className="h-4 w-4" />
														{t(
															"bulk_reschedule.row.reset.label",
															"Reset Changes",
														)}
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													<DropdownMenuLabel className="uppercase text-xs text-muted-foreground/75">
														{t(
															"bulk_reschedule.row.reset.label",
															"Reset Changes",
														)}
													</DropdownMenuLabel>

													{isDateTimeModified && (
														<DropdownMenuItem
															className="uppercase cursor-pointer"
															onClick={() => applyReset("appointmentDateTime")}
														>
															{t(
																"bulk_reschedule.row.reset.only_datetime",
																"Only Date & Time",
															)}
														</DropdownMenuItem>
													)}

													{isTherapistModified && (
														<DropdownMenuItem
															className="uppercase cursor-pointer"
															onClick={() => applyReset("therapist")}
														>
															{t(
																"bulk_reschedule.row.reset.only_therapist",
																"Only Therapist",
															)}
														</DropdownMenuItem>
													)}

													<DropdownMenuSeparator />
													<DropdownMenuItem
														className="uppercase text-destructive focus:text-destructive focus:bg-destructive/5 cursor-pointer"
														onClick={() => setResetConfirmation("all")}
													>
														{t("bulk_reschedule.row.reset.all", "All Data")}
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										)}
									</div>
								</div>
							</div>

							<AlertDialog
								open={resetConfirmation === "all"}
								onOpenChange={(open) => !open && setResetConfirmation(null)}
							>
								<AlertDialogContent>
									<AlertDialogHeader>
										<AlertDialogTitle className="flex items-center gap-1.5">
											<AlertCircle className="size-5 text-destructive" />
											{t(
												"bulk_reschedule.dialogs.reset.title",
												"Reset all changes?",
											)}
										</AlertDialogTitle>
										<AlertDialogDescription>
											{t("bulk_reschedule.dialogs.reset.description", {
												number: appointment.visitNumber,
												defaultValue: `This will restore the original date, time, and therapist for Visit ${appointment.visitNumber}. This action cannot be undone.`,
											})}
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel>
											{t("button.cancel", "Cancel")}
										</AlertDialogCancel>
										<AlertDialogAction
											className="bg-destructive hover:bg-destructive/90"
											onClick={() => {
												applyReset("all");
												setResetConfirmation(null);
											}}
										>
											{t(
												"bulk_reschedule.dialogs.reset.confirm",
												"Confirm Reset",
											)}
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}
